import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as echarts from 'echarts/lib/echarts';
import { get } from 'lodash';
import 'echarts/lib/chart/custom';
import 'echarts/lib/component/tooltip';
import { ChartEvent } from '@components/common/Charts/BaseChart/BaseChart.typed';

import { timelineDataSelector } from '@store/timeline/data/selectors';
import { configGranularitySelector } from '@store/timeline/config/selectors';
import { changeDetailHighlight } from '@store/timeline/config/actions';
import { TIMELINE_DIMENSION } from '@components/session-timeline/utils/constants';
import getXaisValueFromPixel from '../../utils/getXaisValueFromPixel';
import generateEventOption from './utils/generateEventOption';
import generateStateOption from './utils/generateStateOption';
import useChartSize from './hooks/useChartSize';
import {
  Wrapper, StyledEventBaseChart, StyledStateBaseChart, Header, EventLabel, StateLabel,
} from './styled';
import Legends from './Legends';
import { Main } from '../TimelineEvent/styled';

interface IProps {
  dateRange: number[];
}

const SessionDetail = ({ dateRange }: IProps) => {
  const dispatch = useDispatch();
  const stateContainer = React.useRef<HTMLDivElement>(null);
  const timelineData = useSelector(timelineDataSelector);
  const granularity = useSelector(configGranularitySelector);

  const sessionStateData = get(timelineData, [TIMELINE_DIMENSION.SessionState], []);
  const sessionEventData = get(timelineData, [TIMELINE_DIMENSION.SessionEvent], []);
  const [eventChartSize, eventContainer] = useChartSize(dateRange);

  const eventWidth = get(eventChartSize, ['width'], 0);
  const overlayRange = get(eventChartSize, ['overlayRange'], 0);
  const eventOption = generateEventOption({
    range: overlayRange,
    eventData: sessionEventData,
    dateRange,
    chartWidth: eventWidth,
    granularity,
  });

  const stateOption = generateStateOption({
    stateData: sessionStateData,
    dateRange,
    timelineData,
    granularity,
  });

  const events: ChartEvent[] = [
    [
      'click',
      (params) => {
        if (!stateContainer.current) {
          return;
        }
        const instance = echarts.getInstanceByDom(stateContainer.current);
        if (!instance) {
          return;
        }
        const offsetX = get(params, ['event', 'offsetX'], 0);
        const [accurateHighlightTime] = getXaisValueFromPixel(
          offsetX,
          instance,
        );
        dispatch(changeDetailHighlight(accurateHighlightTime));
      },
    ],
  ];

  return (
    <Wrapper>
      <Header>Session Detail</Header>
      <Legends />
      <Main>
        <div className="left">
          <EventLabel>Events</EventLabel>
        </div>
        <div className="right">
          <StyledEventBaseChart className="color-critical" option={eventOption} ref={eventContainer} renderer="svg" />
        </div>
      </Main>
      <Main>
        <div className="left">
          <StateLabel>States</StateLabel>
        </div>
        <div className="right">
          <StyledStateBaseChart className="color-critical" option={stateOption} events={events} ref={stateContainer} renderer="svg" />
        </div>
      </Main>
    </Wrapper>
  );
};

export default SessionDetail;
